import { reportAppError, reportAppEvent } from "../utils/CoreReport";
import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  BrowserPixelEntry,
  BrowserPixelsManifest,
} from "./BrowserPixelAdapter";
import { GtagAdapter } from "./adapters/GtagAdapter";
import { UetAdapter } from "./adapters/UetAdapter";
import { MetaPixelAdapter } from "./adapters/MetaPixelAdapter";
import { OaiqPixelAdapter } from "./adapters/OaiqPixelAdapter";
import { WhopPixelAdapter } from "./adapters/WhopPixelAdapter";
import { TiktokPixelAdapter } from "./adapters/TiktokPixelAdapter";
import { SnapPixelAdapter } from "./adapters/SnapPixelAdapter";
import { TaboolaPixelAdapter } from "./adapters/TaboolaPixelAdapter";
import type { MatchIdentity } from "./AdvancedMatching";
import { isOncePerPage } from "./SemanticEvents";

export { PixelManager };
export type { FiredPixelRef, PixelManagerOptions };

/**
 * Orquestador de los píxeles de navegador.
 *
 * Lee el manifest que api-marketing materializa y api-core entrega dentro de
 * config-values (`browserPixels`), carga un adaptador por red, inicializa
 * cada pixel_id UNA vez y, cuando el SDK emite un evento semántico, lo
 * reparte a todos los píxeles que lo tengan mapeado.
 *
 * Disciplina (la misma que TagManager): NADA de esto puede afectar al lead ni
 * a la navegación. Todo va envuelto en try/catch y los fallos se reportan por
 * CoreReport (Sentry de primera parte, guard tardío), nunca se propagan.
 */

// Referencia compacta de un disparo, tal como viaja en
// `custom_data.pixels` de los eventos de analytics (n = network slug,
// p = pixel_id, e = nombre del evento en la red). Nombres cortos a propósito:
// se repite en cada evento de ClickHouse.
interface FiredPixelRef {
  n: string;
  p: string;
  e: string;
}

// Una confirmación de envío pendiente: la red (`ad_network.slug`) que la dio y
// si su etiqueta guarda un clic propio del visitante
interface PendingSend {
  network: string;
  sent: Promise<void>;
  ownClick: boolean;
}

interface PixelManagerOptions {
  // Identidad conocida al cargar la página (u_form_data de un optin anterior
  // + visitor_id). El optin de ESTA página la aporta al disparar, pero llega
  // tarde para su propio evento: Meta no re-inicializa (ver MetaPixelAdapter) y
  // el re-init de OpenAI y el identify de TikTok resuelven DESPUÉS del track
  // (hash asíncrono). Solo mejora los eventos posteriores de la misma página.
  // Whop y Snapchat son la excepción: su identify / re-init es síncrono (en
  // claro; hashea el propio píxel) y fire() llama a init antes que a track,
  // así que su Lead / SIGN_UP ya sale con la identidad del optin.
  identity?: MatchIdentity | null;
  // Inyectable para test; por defecto window.dataLayer.push
  dataLayerPush?: ((payload: Record<string, unknown>) => void) | null;
  // Inyectable para test; por defecto los adaptadores reales
  adapters?: BrowserPixelAdapter[] | null;
}

// Techo de la lista que viaja en custom_data: una página con muchos eventos
// no puede engordar indefinidamente el payload de analytics.
const MAX_FIRED_REFS = 50;

// Versión del contrato del manifest que este bundle entiende. El SDK vive
// cacheado en las landings publicadas, así que un manifest de una versión
// FUTURA se ignora entero: no inyectar nada es el único comportamiento seguro
// cuando no se entiende la forma del documento.
const MANIFEST_VERSION = 1;

/**
 * Fusión de identidad para el advanced matching.
 *
 * `buildIdentity` devuelve SIEMPRE las cinco claves y rellena con "" las que
 * el formulario no trae, así que un merge a pelo haría que un optin de solo
 * email BORRASE el teléfono y el nombre que ya se conocían del `u_form_data`.
 * Solo se pisa lo que tiene valor.
 */
function mergeIdentity(
  current: MatchIdentity | null,
  incoming: MatchIdentity,
): MatchIdentity {
  const merged: MatchIdentity = { ...(current ?? {}) };
  for (const key of Object.keys(incoming) as (keyof MatchIdentity)[]) {
    const value = incoming[key];
    if (typeof value === "string" && value !== "") {
      merged[key] = value;
    }
  }
  return merged;
}

class PixelManager {
  private _pixels: BrowserPixelEntry[] = [];
  private _adapters: Record<string, BrowserPixelAdapter> = {};
  private _firedSlugs: Record<string, boolean> = {};
  private _fired: FiredPixelRef[] = [];
  // Confirmaciones de envío de la última llamada a fire(), con su red (ver
  // sendsOfLastFire)
  private _lastSends: PendingSend[] = [];
  private _identity: MatchIdentity | null;
  private _dataLayerPush: ((payload: Record<string, unknown>) => void) | null;
  // Una señal por adaptador y carga de página (ver _buildAdapters)
  private _reportedScriptErrors: Record<string, boolean> = {};

  constructor(
    manifest: BrowserPixelsManifest | null | undefined,
    options: PixelManagerOptions = {},
  ) {
    this._identity = options.identity ?? null;
    this._dataLayerPush = options.dataLayerPush ?? null;

    try {
      this._pixels = this._readManifest(manifest);
      if (this._pixels.length === 0) {
        return;
      }
      this._buildAdapters(options.adapters ?? null);
      this._loadAndInit();
    } catch (error) {
      this._pixels = [];
      reportAppError(error, { app_phase: "pixels" });
    }
  }

  /**
   * Lo disparado en esta carga de página (para `custom_data.pixels`).
   *
   * Acumulada y SOLO de añadir (hasta MAX_FIRED_REFS): una posición no cambia
   * nunca de dueño. En eso se apoya la cuenta de disparos en ClickHouse —un
   * disparo = (page_load_id, posición)—, así que ni reordenar, ni quitar, ni
   * vaciar la lista a mitad de la carga.
   */
  get firedPixels(): FiredPixelRef[] {
    return this._fired.slice();
  }

  /** Píxeles activos del manifest (diagnóstico). */
  get pixelCount(): number {
    return this._pixels.length;
  }

  /**
   * Dispara un evento semántico (slug de system_conversion_event) en todos
   * los píxeles que lo tengan mapeado, con el MISMO event_id que usará la
   * CAPI, y lo publica en el dataLayer como `pd_conversion` para que las
   * etiquetas de GTM puedan migrar a un trigger único con Event ID.
   *
   * Devuelve lo que salió en ESTA llamada: vacío si no se disparó nada (sin
   * mapping, ya disparado en la página o vendor que revienta). Con eso sabe
   * el collector si el Lead llegó a salir del navegador.
   */
  fire(
    slug: string,
    eventId: string,
    identity?: MatchIdentity | null,
  ): FiredPixelRef[] {
    this._lastSends = [];
    try {
      if (!slug || this._pixels.length === 0) {
        return [];
      }
      // page_view / content_view / video_watched_50: uno por carga de página.
      if (isOncePerPage(slug)) {
        if (this._firedSlugs[slug]) {
          return [];
        }
        this._firedSlugs[slug] = true;
      }

      if (identity) {
        this._identity = mergeIdentity(this._identity, identity);
      }

      const firedNow: FiredPixelRef[] = [];
      const sends: PendingSend[] = [];

      for (const pixel of this._pixels) {
        const payloads = pixel.events[slug];
        if (!Array.isArray(payloads) || payloads.length === 0) {
          continue;
        }
        const adapter = this._adapters[pixel.adapter];
        if (!adapter) {
          continue;
        }
        if (identity) {
          // El optin aporta identidad: las redes que soportan re-init la
          // aprovechan (OpenAI y TikTok, aunque su hash asíncrono llega
          // DETRÁS de este disparo; Whop, con su identify síncrono, ANTES); las
          // que no, lo ignoran (Meta). Este init tiene que ir antes que el track.
          try {
            adapter.init(pixel.pixel_id, this._identityFor(pixel));
          } catch (error) {
            reportAppError(error, {
              app_phase: "pixels",
              app_network: pixel.network,
            });
          }
        }
        for (const payload of payloads) {
          try {
            const sent = adapter.track(pixel.pixel_id, payload, eventId);
            if (sent && typeof sent.then === "function") {
              sends.push({
                network: pixel.network,
                sent,
                ownClick: this._hasOwnClick(adapter),
              });
            }
            firedNow.push({
              n: pixel.network,
              p: pixel.pixel_id,
              e: payload.name,
            });
          } catch (error) {
            reportAppError(error, {
              app_phase: "pixels",
              app_network: pixel.network,
            });
          }
        }
      }

      if (firedNow.length === 0) {
        return [];
      }

      for (const ref of firedNow) {
        if (this._fired.length < MAX_FIRED_REFS) {
          this._fired.push(ref);
        }
      }
      this._pushConversionToDataLayer(slug, eventId, firedNow);
      this._lastSends = sends;
      return firedNow;
    } catch (error) {
      reportAppError(error, { app_phase: "pixels" });
      return [];
    }
  }

  /**
   * Lo que falta por salir del último fire(): una promesa que se resuelve
   * cuando los adaptadores que saben confirmarlo dan su petición por enviada,
   * o `null` si no hay ninguna que esperar. Nunca rechaza y no caduca: el tope
   * lo pone quien espera.
   *
   * El manifest es de TODA la BU: el evento sale en todas sus redes, venga de
   * donde venga el visitante. Con `cameFrom` solo cuentan las redes de las que
   * viene: aquellas para las que devuelve `true` y aquellas cuya etiqueta
   * guarda un clic propio (`hasOwnClick` del adaptador).
   */
  sendsOfLastFire(
    cameFrom?: (network: string) => boolean,
  ): Promise<void> | null {
    const sends = this._lastSends
      .filter((send) => !cameFrom || send.ownClick || cameFrom(send.network))
      .map((send) => send.sent);
    if (sends.length === 0) {
      return null;
    }
    return Promise.all(sends).then(
      () => undefined,
      () => undefined,
    );
  }

  private _hasOwnClick(adapter: BrowserPixelAdapter): boolean {
    try {
      return adapter.hasOwnClick?.() === true;
    } catch {
      return false;
    }
  }

  /**
   * Consentimiento (GDPR). v1 implícito — el mismo estado que hoy tiene GTM —
   * pero expuesto para cuando haya CMP: `EventCollector.pixels.setConsent(false)`.
   */
  setConsent(granted: boolean): void {
    for (const key of Object.keys(this._adapters)) {
      try {
        this._adapters[key].setConsent(granted);
      } catch (error) {
        reportAppError(error, { app_phase: "pixels", app_adapter: key });
      }
    }
  }

  /**
   * Valida el manifest y descarta lo que no cuadre, píxel a píxel: un manifest
   * a medias debe seguir disparando los píxeles que sí son correctos.
   *
   * SILENCIO por defecto. "Esta BU no tiene píxeles de navegador" es el estado
   * NORMAL de casi todo el parque y llega de CUATRO formas indistinguibles
   * desde aquí: la clave ausente (api-core antiguo o BU sin módulo de ads), el
   * documento `null` (api-core no pudo agregar), `pixels: []` y `pixels: null`
   * — el serializador del código generado convierte toda colección vacía en
   * `null`. Alarmar en cualquiera de ellas significaba un evento a Sentry POR
   * CARGA DE PÁGINA en todo el parque: se quemaba la cuota con una falsa
   * alarma y con ella la telemetría que sostiene la defensa del lead.
   *
   * La telemetría queda para lo que de verdad no se entiende: un documento que
   * no es un objeto, un `pixels` que no es ni array ni `null`, o una versión
   * del contrato posterior a la que este bundle sabe leer.
   */
  private _readManifest(
    manifest: BrowserPixelsManifest | null | undefined,
  ): BrowserPixelEntry[] {
    if (manifest === undefined || manifest === null) {
      return [];
    }
    if (typeof manifest !== "object" || Array.isArray(manifest)) {
      this._reportManifestIssue("corrupt");
      return [];
    }

    const document = manifest as unknown as Record<string, unknown>;

    // Sin `version` se asume la 1: la clave la pone marketing siempre y
    // descartar por su ausencia apagaría píxeles que funcionan.
    const version = document["version"];
    if (typeof version === "number" && version > MANIFEST_VERSION) {
      this._reportManifestIssue("version");
      return [];
    }

    const pixels = document["pixels"];
    if (pixels === undefined || pixels === null) {
      // BU sin píxeles de navegador: el caso normal, en silencio.
      return [];
    }
    if (!Array.isArray(pixels)) {
      this._reportManifestIssue("corrupt");
      return [];
    }

    const valid: BrowserPixelEntry[] = [];
    for (const candidate of pixels as BrowserPixelEntry[]) {
      if (
        !candidate ||
        typeof candidate !== "object" ||
        typeof candidate.adapter !== "string" ||
        typeof candidate.pixel_id !== "string" ||
        candidate.pixel_id === "" ||
        !candidate.events ||
        typeof candidate.events !== "object"
      ) {
        continue;
      }
      valid.push({
        network: typeof candidate.network === "string" ? candidate.network : "",
        adapter: candidate.adapter,
        pixel_id: candidate.pixel_id,
        options: candidate.options ?? null,
        events: this._readEvents(candidate.events),
      });
    }
    return valid;
  }

  private _reportManifestIssue(reason: string): void {
    reportAppEvent("pixel_manifest_missing", {
      app_phase: "pixels",
      app_reason: reason,
    });
  }

  /**
   * `options.advanced_matching` del manifest: el interruptor que decide si a
   * ESTE píxel se le manda identidad del visitante. Hoy el backend lo emite
   * siempre a `true`, pero estaba sin cablear — y un campo del contrato que no
   * gobierna nada es una promesa falsa: el día que una red declare `false`, el
   * SDK le seguiría mandando el email en cada init.
   */
  private _identityFor(pixel: BrowserPixelEntry): MatchIdentity | null {
    if (pixel.options && pixel.options["advanced_matching"] === false) {
      return null;
    }
    return this._identity;
  }

  // `events[slug]` es SIEMPRE un array en el contrato, pero se tolera el
  // objeto suelto por si un manifest viejo llega sin normalizar.
  private _readEvents(
    events: Record<string, unknown>,
  ): Record<string, BrowserEventPayload[]> {
    const parsed: Record<string, BrowserEventPayload[]> = {};
    for (const slug of Object.keys(events)) {
      const raw = events[slug];
      const list = Array.isArray(raw) ? raw : [raw];
      const payloads: BrowserEventPayload[] = [];
      for (const item of list) {
        const payload = item as BrowserEventPayload | null;
        if (!payload || typeof payload.name !== "string" || payload.name === "") {
          continue;
        }
        payloads.push({
          name: payload.name,
          custom: payload.custom === true,
          params:
            payload.params && typeof payload.params === "object"
              ? payload.params
              : {},
        });
      }
      if (payloads.length > 0) {
        parsed[slug] = payloads;
      }
    }
    return parsed;
  }

  private _buildAdapters(injected: BrowserPixelAdapter[] | null): void {
    const onScriptError = (adapterKey: string, error: unknown) => {
      // Distingue "ad blocker / CDN del vendor caída" de "no había nada que
      // disparar": sin esta señal ambos casos se ven igual en los informes.
      //
      // Va por reportAppEvent (nivel info) y UNA vez por adaptador y carga de
      // página, como storage_fallback: que un ad blocker tumbe fbevents.js es
      // una condición ESPERADA en una fracción grande del tráfico, no una
      // excepción de la página — mandarla por captureException ahogaba el
      // canal con el que se detecta la pérdida de leads. La magnitud del
      // fenómeno ya se mide sin Sentry en `custom_data.ad_blocker`.
      if (this._reportedScriptErrors[adapterKey]) {
        return;
      }
      this._reportedScriptErrors[adapterKey] = true;
      reportAppEvent("pixel_script_error", {
        app_phase: "pixels",
        app_adapter: adapterKey,
        app_detail: String((error as { type?: string })?.type ?? "error"),
      });
    };

    const available: BrowserPixelAdapter[] = injected ?? [
      new MetaPixelAdapter(onScriptError),
      new OaiqPixelAdapter(onScriptError),
      new WhopPixelAdapter(onScriptError),
      new TiktokPixelAdapter(onScriptError),
      new SnapPixelAdapter(onScriptError),
      new GtagAdapter(onScriptError),
      new UetAdapter(onScriptError),
      new TaboolaPixelAdapter(onScriptError),
    ];

    const needed: Record<string, boolean> = {};
    for (const pixel of this._pixels) {
      needed[pixel.adapter] = true;
    }
    for (const adapter of available) {
      if (needed[adapter.key]) {
        this._adapters[adapter.key] = adapter;
      }
    }
  }

  private _loadAndInit(): void {
    const initialized: Record<string, boolean> = {};
    for (const key of Object.keys(this._adapters)) {
      try {
        this._adapters[key].load();
      } catch (error) {
        reportAppError(error, { app_phase: "pixels", app_adapter: key });
      }
    }
    for (const pixel of this._pixels) {
      const adapter = this._adapters[pixel.adapter];
      if (!adapter) {
        continue;
      }
      // El mismo píxel puede venir en varios canales: un solo init.
      const key = pixel.adapter + ":" + pixel.pixel_id;
      if (initialized[key]) {
        continue;
      }
      initialized[key] = true;
      try {
        adapter.init(pixel.pixel_id, this._identityFor(pixel));
      } catch (error) {
        reportAppError(error, {
          app_phase: "pixels",
          app_network: pixel.network,
        });
      }
    }
  }

  // Coexistencia con GTM: el contenedor puede escuchar `pd_conversion` y usar
  // `pd_event_id` como Event ID en sus propias etiquetas mientras dure la
  // transición — así sus copias también deduplican contra nuestro CAPI.
  private _pushConversionToDataLayer(
    slug: string,
    eventId: string,
    fired: FiredPixelRef[],
  ): void {
    try {
      const payload = {
        event: "pd_conversion",
        pd_event: slug,
        pd_event_id: eventId,
        pd_pixels: fired.map((ref) => ({
          network: ref.n,
          pixel_id: ref.p,
          name: ref.e,
        })),
      };
      if (this._dataLayerPush) {
        this._dataLayerPush(payload);
        return;
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {
      // GTM jamás bloquea nada (lección del incidente de julio de 2026)
    }
  }
}
